import {
  ScenarioPackage,
  ScenarioStep,
  GrpcCall,
  ScenarioElement,
  ScenarioLogs,
  RevitContext,
  EnvironmentInfo,
  SCENARIO_SCHEMA_VERSION,
} from '../contract';
import type { RecorderSnapshot, RecordedStep, RecordingMode } from '../recording/scenarioRecorder';
import type { RecordedGrpcCall } from '../recording/recordingSink';
import {
  buildErrorsLogLines,
  buildGrpcLogLines,
  ERRORS_LOG_REF,
  GRPC_LOG_REF,
  RECORDER_LOG_REF,
} from './recorderLogArtifacts';

/** Externalized artifacts for one element (from the element-snapshot mapper). */
export interface ScenarioElementArtifact {
  element: ScenarioElement;
  elementRef: string;
  elementJson: string;
  parametersJson: string;
}

/** A bundled model copy (Full mode only). */
export interface ScenarioModelArtifact {
  archiveRef: string;
  data: Uint8Array;
  linkedModels?: Array<{ archiveRef: string; data: Uint8Array }>;
}

export interface ScenarioScreenshot {
  ref: string;
  data: Uint8Array;
}

/** Everything the builder needs. In tests these are synthetic buffers. */
export interface ScenarioArchiveInput {
  scenario: { id: string; name: string; createdAt: string; createdBy: string; description?: string };
  environment: EnvironmentInfo;
  mode: RecordingMode;
  snapshot: RecorderSnapshot;
  revit?: RevitContext;
  elements?: ScenarioElementArtifact[];
  pluginSettingsJson?: string;
  webSettings?: unknown;
  featureFlags?: Record<string, unknown>;
  logLines?: string[];
  model?: ScenarioModelArtifact;
  screenshots?: ScenarioScreenshot[];
  /**
   * Step-index → screenshot ref map (card G). Each entry stamps
   * `step.screenshotRef` and is ref-integrity-checked against {@link screenshots}.
   */
  screenshotRefs?: Record<number, string>;
}

export interface ScenarioArchiveValidation {
  ok: boolean;
  /** Mandatory files (spec §10) that are missing. */
  missing: string[];
  /** Relative refs in scenario.json with no backing file in the archive. */
  unresolvedRefs: string[];
}

export interface ScenarioArchiveResult {
  scenarioPackage: ScenarioPackage;
  files: Record<string, string | Uint8Array>;
  validation: ScenarioArchiveValidation;
}

/** Files every scenario package must contain before it can be finalized (spec §10). */
export const MANDATORY_ARCHIVE_FILES = [
  'scenario.json',
  'README.md',
  'settings/plugin-settings.json',
  'logs/recorder.log',
] as const;

function pad3(n: number): string {
  return String(n).padStart(3, '0');
}

function toScenarioStep(step: RecordedStep, screenshotRefs?: Record<number, string>): ScenarioStep {
  const out: ScenarioStep = { index: step.index, type: step.type, action: step.action };
  if (step.target !== undefined) {
    out.target = step.target;
  }
  if (step.timestamp !== undefined) {
    out.timestamp = step.timestamp;
  }
  const details: Record<string, unknown> = { ...(step.details ?? {}) };
  if (step.correlationIds && step.correlationIds.length > 0) {
    details.correlationIds = step.correlationIds;
  }
  if (Object.keys(details).length > 0) {
    out.details = details;
  }
  const screenshotRef = screenshotRefs?.[step.index];
  if (screenshotRef) {
    out.screenshotRef = screenshotRef;
  }
  return out;
}

/**
 * Assemble the scenario package: the scenario.json object + the full file map
 * (relative refs only) + a mandatory-contents / ref-integrity validation.
 * Pure — no DOM, no zip.
 */
export function buildScenarioArchive(input: ScenarioArchiveInput): ScenarioArchiveResult {
  const lightweight = input.mode === 'lightweight';
  const files: Record<string, string | Uint8Array> = {};

  const steps = input.snapshot.steps.map((step) => toScenarioStep(step, input.screenshotRefs));

  // gRPC: every payload lands as a file. Externalized blobs come via
  // snapshot.artifacts; small inline payloads are externalized here too.
  for (const [ref, content] of Object.entries(input.snapshot.artifacts ?? {})) {
    files[ref] = content;
  }
  const grpc: GrpcCall[] = input.snapshot.grpc.map((call) => toGrpcCall(call, files));

  // Elements + their externalized files.
  const elements: ScenarioElement[] = [];
  for (const artifact of input.elements ?? []) {
    elements.push(artifact.element);
    files[artifact.elementRef] = artifact.elementJson;
    if (artifact.element.parametersRef) {
      files[artifact.element.parametersRef] = artifact.parametersJson;
    }
  }

  // Revit context + model (Full bundles a copy; Lightweight is reference-only).
  let revit: RevitContext | undefined;
  if (input.revit) {
    revit = { ...input.revit, document: { ...input.revit.document } };
    if (!lightweight && input.model) {
      files[input.model.archiveRef] = input.model.data;
      revit.document.modelPathRef = input.model.archiveRef;
      for (const link of input.model.linkedModels ?? []) {
        files[link.archiveRef] = link.data;
      }
    } else {
      // Lightweight or no copy: do not bundle a model file.
      delete revit.document.modelPathRef;
    }
  }

  // Settings. Explicit inputs win; otherwise fall back to what the recorder
  // buffer collected (the settings/environment-collection card stashes these).
  if (input.pluginSettingsJson) {
    files['settings/plugin-settings.json'] = input.pluginSettingsJson;
  }
  const webSettings = input.webSettings ?? input.snapshot.settings?.webSettings;
  if (webSettings !== undefined) {
    files['settings/web-settings.json'] = JSON.stringify(webSettings, null, 2);
  }
  const featureFlags = input.featureFlags ?? input.snapshot.settings?.featureFlags;
  if (featureFlags !== undefined) {
    files['settings/feature-flags.json'] = JSON.stringify(featureFlags, null, 2);
  }
  // environment.json — the self-describing version sidecar. Prefer the collected
  // document (carries its own schemaVersion); else wrap the inline environment.
  const environmentDocument = input.snapshot.settings?.environment ?? {
    schemaVersion: SCENARIO_SCHEMA_VERSION,
    ...input.environment,
  };
  files['settings/environment.json'] = JSON.stringify(environmentDocument, null, 2);

  // Recorder diagnostics logs (logs/). recorder.log + errors.log are written in
  // both modes; grpc.log (per-call detail) is Full-only, mirroring Lightweight's
  // "no big gRPC snapshots" rule. Each log is referenced from scenario.json.logs.
  const logs: ScenarioLogs = {};
  if (input.logLines && input.logLines.length > 0) {
    files[RECORDER_LOG_REF] = `${input.logLines.join('\n')}\n`;
    logs.recorderLog = RECORDER_LOG_REF;
  }
  if (!lightweight) {
    const grpcLogLines = buildGrpcLogLines(input.snapshot.grpc);
    if (grpcLogLines.length > 0) {
      files[GRPC_LOG_REF] = `${grpcLogLines.join('\n')}\n`;
      logs.grpcLog = GRPC_LOG_REF;
    }
  }
  const errorsLogLines = buildErrorsLogLines({
    recorderLogLines: input.logLines,
    warnings: input.snapshot.warnings,
    grpcCalls: input.snapshot.grpc,
  });
  if (errorsLogLines.length > 0) {
    files[ERRORS_LOG_REF] = `${errorsLogLines.join('\n')}\n`;
    logs.errorsLog = ERRORS_LOG_REF;
  }

  // Screenshots.
  for (const shot of input.screenshots ?? []) {
    files[shot.ref] = shot.data;
  }

  const scenarioPackage: ScenarioPackage = {
    schemaVersion: SCENARIO_SCHEMA_VERSION,
    scenario: {
      id: input.scenario.id,
      name: input.scenario.name,
      createdAt: input.scenario.createdAt,
      createdBy: input.scenario.createdBy,
      ...(input.scenario.description ? { description: input.scenario.description } : {}),
    },
    environment: input.environment,
    steps,
  };
  if (revit) {
    scenarioPackage.revit = revit;
  }
  if (elements.length > 0) {
    scenarioPackage.elements = elements;
  }
  if (grpc.length > 0) {
    scenarioPackage.grpc = grpc;
  }
  if (input.snapshot.warnings && input.snapshot.warnings.length > 0) {
    scenarioPackage.warnings = [...input.snapshot.warnings];
  }
  if (Object.keys(logs).length > 0) {
    scenarioPackage.logs = logs;
  }

  files['scenario.json'] = JSON.stringify(scenarioPackage, null, 2);
  files['README.md'] = generateReadme(scenarioPackage, input.mode, files);

  const validation = validateArchive(scenarioPackage, files);

  // Self-flag an incomplete archive so a stopped-after-failure recording still
  // produces a clearly-marked partial package (gaps explicit in scenario.json +
  // README). Complete archives are untouched.
  if (!validation.ok) {
    const reasons: string[] = [];
    if (validation.missing.length > 0) {
      reasons.push(`missing ${validation.missing.join(', ')}`);
    }
    if (validation.unresolvedRefs.length > 0) {
      reasons.push(`unresolved refs ${validation.unresolvedRefs.join(', ')}`);
    }
    const flag = `INCOMPLETE ARCHIVE — ${reasons.join('; ')}.`;
    scenarioPackage.warnings = [flag, ...(scenarioPackage.warnings ?? [])];
    files['scenario.json'] = JSON.stringify(scenarioPackage, null, 2);
    files['README.md'] = generateReadme(scenarioPackage, input.mode, files);
  }

  return { scenarioPackage, files, validation };
}

function toGrpcCall(call: RecordedGrpcCall, files: Record<string, string | Uint8Array>): GrpcCall {
  let requestRef = call.requestRef;
  if (!requestRef && call.request !== undefined) {
    requestRef = `grpc/step-${pad3(call.index)}-request.json`;
    files[requestRef] = JSON.stringify(call.request, null, 2);
  }
  let responseRef = call.responseRef;
  if (!responseRef && call.response !== undefined) {
    responseRef = `grpc/step-${pad3(call.index)}-response.json`;
    files[responseRef] = JSON.stringify(call.response, null, 2);
  }

  const out: GrpcCall = { index: call.index, method: call.method };
  if (requestRef !== undefined) {
    out.requestRef = requestRef;
  }
  if (responseRef !== undefined) {
    out.responseRef = responseRef;
  }
  if (call.status !== undefined) {
    out.status = call.status;
  }
  if (call.durationMs !== undefined) {
    out.durationMs = call.durationMs;
  }
  return out;
}

/** Collect every relative ref declared in scenario.json. */
function collectRefs(pkg: ScenarioPackage): string[] {
  const refs: string[] = [];
  if (pkg.revit?.document.modelPathRef) {
    refs.push(pkg.revit.document.modelPathRef);
  }
  for (const step of pkg.steps) {
    if (step.screenshotRef) {
      refs.push(step.screenshotRef);
    }
  }
  for (const element of pkg.elements ?? []) {
    if (element.parametersRef) {
      refs.push(element.parametersRef);
    }
  }
  for (const call of pkg.grpc ?? []) {
    if (call.requestRef) {
      refs.push(call.requestRef);
    }
    if (call.responseRef) {
      refs.push(call.responseRef);
    }
  }
  if (pkg.logs) {
    for (const ref of [pkg.logs.recorderLog, pkg.logs.grpcLog, pkg.logs.errorsLog]) {
      if (ref) {
        refs.push(ref);
      }
    }
  }
  return refs;
}

function validateArchive(
  pkg: ScenarioPackage,
  files: Record<string, string | Uint8Array>,
): ScenarioArchiveValidation {
  const missing = MANDATORY_ARCHIVE_FILES.filter((name) => !(name in files));
  const unresolvedRefs = collectRefs(pkg).filter((ref) => !(ref in files));
  return { ok: missing.length === 0 && unresolvedRefs.length === 0, missing, unresolvedRefs };
}

function generateReadme(pkg: ScenarioPackage, mode: RecordingMode, files: Record<string, string | Uint8Array>): string {
  const lines: string[] = [
    `# Scenario: ${pkg.scenario.name}`,
    '',
    `- **Id:** ${pkg.scenario.id}`,
    `- **Created:** ${pkg.scenario.createdAt} by ${pkg.scenario.createdBy}`,
    `- **Mode:** ${mode}`,
    `- **Schema:** ${pkg.schemaVersion}`,
    `- **App:** ${pkg.environment.appVersion} · web ${pkg.environment.webClientVersion} · plugin ${pkg.environment.revitPluginVersion} · Revit ${pkg.environment.revitVersion}`,
    '',
    '## Contents',
    `- Steps: ${pkg.steps.length}`,
    `- gRPC calls: ${pkg.grpc?.length ?? 0}`,
    `- Elements: ${pkg.elements?.length ?? 0}`,
    `- Model: ${pkg.revit?.document.modelPathRef ?? 'reference only (not bundled)'}`,
  ];
  if (pkg.scenario.description) {
    lines.push('', '## Description', pkg.scenario.description);
  }
  if (pkg.warnings && pkg.warnings.length > 0) {
    lines.push('', '## Warnings — some data could not be collected');
    for (const warning of pkg.warnings) {
      lines.push(`- ${warning}`);
    }
  }
  lines.push(
    '',
    '## How to replay',
    'This package is self-contained — a test runner can reproduce the recorded',
    'scenario from it. The consumption protocol is the replay-reader contract:',
    '`contracts/test-records/replay-reader.md`.',
    '',
    '1. Unpack this `.zip` to a working directory (root holds `scenario.json` +',
    '   this `README.md`; artifacts live under `model/`, `settings/`, `grpc/`,',
    '   `elements/`, `logs/`, `screenshots/`).',
    '2. Open the model — if `revit.document.modelPathRef` points at a bundled',
    '   `.rvt`, open it; otherwise resolve an equivalent model from',
    '   `revit.document`.',
    '3. Boot the live stack as the live-e2e suite does (`pipeex-e2e-launcher` +',
    '   Playwright Chromium against the web client).',
    '4. Load `scenario.json` and validate it against',
    '   `contracts/test-records/scenario.schema.json`.',
    '5. Replay `steps[]` in `index` order: drive `ui` steps by their',
    '   `data-testid` target, observe `grpc` steps, reproduce `revit` steps.',
    '6. Evaluate `assertions[]` against the live replay context.',
  );
  lines.push('', '## Files', ...Object.keys(files).sort().map((name) => `- \`${name}\``), '');
  return lines.join('\n');
}
